export enum Status {
  DONE = 'done',
  WAITING = 'waiting',
  TODO = 'todo',
  NONE = 'none',
}

export enum Billing {
  NONE = 'none',
  NEW = 'new',
  MAINTENANCE = 'maintenance',
  DISPOSED = 'disposed',
}

export enum ConditionValue {
  OK = 'ok',
  NOT_OK = 'notok',
  NEW = 'new',
  // Findings specific to one check rather than a generic fault, so the rapport
  // records what was actually wrong (client #97). Which checks offer which
  // values lives in lib/hose-condition.
  DRIPPING = 'dripping',
  RUSTY = 'rusty',
  SANDY = 'sandy',
}

// Section divider colours. The old Excel app offered four fixed colours in its
// "Titelblock" dialog and the technicians already think in them, so this is a
// fixed set rather than a free picker — it also keeps print and dark mode
// predictable. Values map to the .section-colour-* classes in app.css.
export enum SectionColor {
  ONE = 'c1',
  TWO = 'c2',
  THREE = 'c3',
  FOUR = 'c4',
}

export enum PressureClass {
  BOOST = 'boost',
  PERMANENT = 'permanent',
}

export enum WorkerRole {
  SUPERADMIN = 'superadmin',
  ADMIN = 'admin',
  STAFF = 'staff',
  TECHNICIAN = 'technician',
}

// Maps the UI WorkerRole to the backend _users role string used by
// POST/PUT /api/tenants/{id}/users (org_admin | staff | technician).
export const WORKER_ROLE_TO_API: Record<WorkerRole, string> = {
  [WorkerRole.SUPERADMIN]: 'super_admin',
  [WorkerRole.ADMIN]: 'org_admin',
  [WorkerRole.STAFF]: 'staff',
  [WorkerRole.TECHNICIAN]: 'technician',
};

export enum SparePartType {
  EXTINGUISHER = 'extinguisher',
  HOSE = 'hose',
}

export enum VisitStatus {
  PLANNED = 'planned',
  CONFIRMED = 'confirmed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Visit {
  id: string;
  customerId: string;
  date: string;
  // Optional time of day (HH:mm). Most jobs are booked as "some time that
  // day", so this stays unset unless the technician enters one; appointments
  // without a time sort ahead of timed ones (client #93).
  time?: string;
  worker?: string;
  assignedTechId?: string; // technician email (FK = JWT sub = couch userCtx.name)
  status: VisitStatus;
  notes?: string;
  completedAt?: string; // ISO timestamp set when the inspection is completed
}

// A reading taken for one device during an inspection visit — the per-visit
// snapshot of what the technician measured (the legacy Kontrolle rapport).
export interface InspectionReading {
  deviceId: string;
  kind: 'extinguisher' | 'hose';
  note?: string;
  // Extinguisher: overall condition this visit (ok / defect / replaced),
  // refill date, spare parts used, and billing class — the legacy per-visit
  // rapport fields (Füllung ersetzt am / Ersatzteile / Neu-Mat).
  extCondition?: ConditionValue;
  refilled?: string;
  spareParts?: string[];
  billing?: Billing;
  // Hose (SN EN 671-3) readings:
  restingPressure?: string;
  flowPressure?: string;
  flowRate?: string;
  condition?: HoseCondition;
  // Free-text note per condition check, surfaced when that check is Not OK.
  conditionNotes?: Partial<Record<keyof HoseCondition, string>>;
  // Date a condition check's part was replaced (ISO yyyy-mm-dd), set when that
  // check is switched to New and editable afterwards (client #50). Optional and
  // sparse like conditionNotes, so existing hose docs stay valid untouched.
  conditionReplaced?: Partial<Record<keyof HoseCondition, string>>;
  // Per-device photo documentation captured during the visit (stored upload
  // refs — "s3:<key>" or a data: URL fallback; see lib/uploads.ts).
  photos?: string[];
}

// Work in progress for one visit: what the technician has typed into the
// maintenance dialog but not submitted yet. Held in the store (not component
// state) so leaving the dialog — navigating off to add a device, an accidental
// dismiss, a reload on a flaky tablet — never throws the work away (client #64).
export interface InspectionDraft {
  // Per-device reading, keyed by device id. `unchecked` are the devices the
  // technician has un-ticked, i.e. not serviced this visit.
  readings: Record<string, Omit<InspectionReading, 'deviceId' | 'kind'>>;
  unchecked: string[]; // array, not Set — must survive JSON persistence
}

export interface Inspection {
  id: string;
  visitId: string;
  customerId: string;
  technician: string; // email of the completing tech
  date: string; // ISO date (YYYY-MM-DD)
  devicesServiced: string[]; // extinguisher/hose ids serviced this visit
  readings?: InspectionReading[]; // per-device measurements taken this visit
  signatureKey?: string; // S3 key (or data: URL fallback offline)
  notes?: string;
}

export interface Address {
  company?: string;
  addition?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  phones?: string[];
  email?: string;
  attn?: string;
}

export enum ContactRole {
  JANITOR = 'janitor',
  JANITOR_2 = 'janitor2',
  MANAGEMENT = 'management',
  SAFETY_OFFICER = 'safetyOfficer',
  CUSTOM = 'custom',
}

export interface Contact {
  role: ContactRole;
  /** free-text role label, used when role === ContactRole.CUSTOM */
  customRole?: string;
  name?: string;
  phone?: string;
  email?: string;
}

export interface Extinguisher {
  id: string;
  section?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  content?: string;
  type?: string;
  deviceNumber?: string;
  year?: number;
  lastInspection?: string;
  nextInspection?: string;
  refilled?: string;
  // Date the whole device was swapped out for a new one. Auto-stamped with
  // today when the billing class is set to NEW, but kept editable afterwards
  // because the swap is often booked a few days after it happened (client #50).
  replaced?: string;
  billing?: Billing;
  spareParts?: string[];
  photos?: string[];
  notes?: string;
  documentationPdf?: string;
  maintainedThisCycle?: boolean;
}

export interface HoseCondition {
  fireCock: ConditionValue;
  seals: ConditionValue;
  hose: ConditionValue;
  nozzle: ConditionValue;
  axle: ConditionValue;
  swivelArm: ConditionValue;
  doorLock: ConditionValue;
  water: ConditionValue;
}

export interface Hose {
  id: string;
  deviceNumber?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  year?: number;
  length?: number;
  diameter?: string;
  connectionDiameter?: string;
  connection?: string;
  worker?: string; // service technician who last serviced this hose
  // Same billing classification the extinguishers carry, so a hose can be
  // charged as maintenance / new / disposed and the maintenance list ticks
  // it on the same rule (client #91, extending #67).
  billing?: Billing;
  condition: HoseCondition;
  // Free-text note per condition check, surfaced when that check is Not OK.
  conditionNotes?: Partial<Record<keyof HoseCondition, string>>;
  // Date the part behind a condition check was replaced (ISO yyyy-mm-dd),
  // auto-filled when that check is set to New, editable after (client #50).
  conditionReplaced?: Partial<Record<keyof HoseCondition, string>>;
  lastService?: string;
  nextService?: string;
  restingPressure?: string;
  flowPressure?: string;
  flowRate?: string;
  pressureTestDate?: string;
  spareParts?: string[];
  photos?: string[];
  status: Status;
}

export interface Customer {
  id: string;
  name: string;
  objectNr?: string;
  status: Status;
  worker?: string;
  createdBy?: string;
  nextInspection?: string;
  lastInspection?: string;
  created?: string;
  billingAddress?: Address;
  objectAddress?: Address;
  ownerAddress?: Address;
  contacts?: Contact[];
  lat?: number;
  lng?: number;
  notes?: string;
  // PDF/file attachments for the customer notes (stored upload refs).
  notesAttachments?: { name: string; ref: string }[];
  serviceContract?: boolean;
  serviceContractPdf?: string;
  linkedCustomerIds?: string[];
  extinguishers: Extinguisher[];
  hoses: Hose[];
  archives: Archive[];
  deviceCounts?: Record<string, number>;
  // Colour per section divider name, keyed by the section string stored on the
  // devices themselves. Held on the customer so one section has one colour
  // rather than each device carrying its own copy to disagree about.
  sectionColors?: Record<string, SectionColor>;
  hasLinkedFiles?: boolean;
  legacy?: LegacyImport;
}

// Migration bookkeeping from the legacy Excel import (read-only in UI).
export interface LegacyImport {
  sourceSheet: string;
  sourceHash: string;
  unmapped: LegacyUnmapped[];
  linkedFiles?: string[];
}

export interface LegacyUnmapped {
  row?: number;
  col?: number;
  where?: string;
  label?: string;
  value: string;
}

export interface ArchiveDevice {
  deviceNumber?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  content?: string;
  type?: string;
  year?: number;
  lastInspection?: string;
  nextInspection?: string;
  refilled?: string;
  spareParts?: string[];
}

export interface Archive {
  id: string;
  date: string;
  deviceCount: number;
  technician: string;
  devices?: ArchiveDevice[];
}

export interface Manufacturer {
  id: string;
  name: string;
  // Maintenance manual (uploaded once per manufacturer, shared by its models).
  // Kept as the fallback for models that carry no manual of their own (#51).
  documentationPdf?: string;
  models: Model[];
}

export interface Model {
  id: string;
  mfrId: string;
  name: string;
  content: string;
  deviceType: string;
  agentType: string;
  // Catalogue price of the device itself, charged when one is fitted as new
  // (billing = NEW). Without it the bill showed 0 (client #60).
  price?: number;
  // Maintenance manual for this specific device. The client clarified the
  // documentation belongs to the device, not the brand (#51); the
  // manufacturer-level PDF stays as the fallback for models without one.
  documentationPdf?: string;
}

export interface DeviceType {
  id: string;
  nameDe: string;
  nameEn: string;
  pressureClass: PressureClass;
  ageLimit: number;
  // Years between services for this device type. Falls back to
  // settings.inspectionInterval when unset (legacy docs / not configured).
  inspectionInterval?: number;
}

export interface SparePart {
  articleNr: string;
  name: string;
  type: SparePartType;
  price: number;
  mfr?: string; // manufacturer name, for categorising the parts catalogue
  models?: string[];
}

export interface ServicePrice {
  articleNr: string;
  description: string;
  deviceType: string;
  qtyRange: string;
  price: number;
}

export interface Worker {
  id: string;
  name: string;
  email?: string;
  role: WorkerRole;
  active: boolean;
}

export interface HoseConfig {
  hoseDiameters: string[];
  connectionDiameters: string[];
  hoseModels: string[];
  // Coupling type (Storz C, …). Was free text on every hose, so the same
  // coupling got typed a dozen slightly different ways (client #89).
  connections?: string[];
}

export interface Settings {
  country: string;
  companyName: string;
  vatNumber: string;
  street: string;
  zip: string;
  city: string;
  phone: string;
  email: string;
  vatPercent: number;
  currency: string;
  rounding: string;
  paymentTerms: number;
  inspectionInterval: number;
  boostAgeLimit: number;
  permanentAgeLimit: number;
  hoseTestInterval: number;
  travelSurcharge: number;
  iban: string;
  // Legacy (pre-#95) per-role write allow-list (role → allowed doc types).
  // Still read — converted by lib/section-permissions.ts for a role that has
  // no `permissions` entry yet — but never written any more.
  permMatrix?: Record<string, string[]>;
  // Per-role section rights (client #95): Lesen / Erstellen / Bearbeiten /
  // Löschen per app section, edited under Verwaltung → Berechtigungen. Absent
  // = today's defaults. Admins are never listed: they always have every right.
  permissions?: SectionPermissions;
  // Which customers reach a technician's device beyond those assigned to them
  // in the calendar (client #92). Enforced by the server's sync proxy.
  techVisibility?: TechVisibility;
}

export interface TechVisibility {
  // Overdue customers and those due within 30 days, like Erinnerungen.
  dueSoon?: boolean;
  // Every customer: the technician syncs the whole tenant again.
  all?: boolean;
}

export interface SectionRight {
  read: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
}

// Keyed by SectionId (lib/section-permissions.ts). Stored loosely so a doc
// written before a section existed still loads; missing sections fall back to
// the defaults.
export type StoredSectionRights = Partial<
  Record<string, Partial<SectionRight>>
>;

export interface SectionPermissions {
  technician?: StoredSectionRights;
  staff?: StoredSectionRights;
}
